// src/components/portfolio/PortfolioSection.js
import React, { useRef } from 'react';
import { usePortfolioState } from '../../hooks/usePortfolioState';
import PortfolioTree from './PortfolioTree';
import PortfolioModals from './PortfolioModals';
import { updateCategory, updatePortfolioStructure } from '../../services/firebase/portfolioService';
import { getSampleJSONContent, parsePortfolioJSON } from '../../utils/importHelper';
import { FileUp, FileDown, AlertTriangle } from 'lucide-react';

function PortfolioSection({ userId, selectedPortfolioId }) {
  const fileInputRef = useRef(null);
  const {
    portfolioData,
    symbolCache,
    loading,
    modalState,
    setModalState,
    handleAddCategory,
    handleAddAsset,
    handleCategorySubmit,
    handleAssetSubmit,
    handleDelete,
    handleAllocationChange,
    handleDistributeEqually
  } = usePortfolioState(userId, selectedPortfolioId);

  const downloadSampleJSON = () => {
    const content = getSampleJSONContent();
    const blob = new Blob([content], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'portfolio_structure_template.json';
    link.click();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const portfolioItems = parsePortfolioJSON(text);
        
        if (!portfolioItems || portfolioItems.length === 0) {
            throw new Error('İçe aktarılacak yapılandırma bulunamadı.');
        }

        const confirmMsg = "Bu işlem mevcut portföy yapılandırmasını (kategoriler ve hedefler) tamamen yüklediğiniz dosyadakiyle değiştirecek veya güncelleyecektir. Devam etmek istiyor musunuz?";
        if (!window.confirm(confirmMsg)) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        await updatePortfolioStructure(userId, selectedPortfolioId, portfolioItems);

        alert('Portföy yapılandırması başarıyla güncellendi! Sayfa yenileniyor...');
        window.location.reload();
        
      } catch (error) {
        console.error('Import error:', error);
        alert('Hata: ' + error.message);
      }
      
      // Input'u temizle
      if (fileInputRef.current) {
          fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const triggerFileUpload = () => {
      fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#151934] rounded-lg shadow-sm p-4">
      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={downloadSampleJSON}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none"
          title="Örnek JSON İndir"
        >
          <FileDown className="w-4 h-4" />
          Örnek JSON
        </button>
        <button
          onClick={triggerFileUpload}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FileUp className="w-4 h-4" />
          JSON İçe Aktar
        </button>
        <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept=".json" 
            className="hidden" 
        />
      </div>

      <PortfolioTree 
        data={portfolioData}
        symbolCache={symbolCache}  // symbolCache'i prop olarak geçiyoruz
        onAddAsset={handleAddAsset}
        onAddCategory={handleAddCategory}
        onDelete={handleDelete}
        onAllocationChange={handleAllocationChange}
        onDistributeEqually={handleDistributeEqually}
      />

      <PortfolioModals
        modalState={modalState}
        setModalState={setModalState}
        onCategorySubmit={handleCategorySubmit}
        onAssetSubmit={handleAssetSubmit}
        portfolioData={portfolioData} // Eklenen prop
      />
    </div>
  );
}

export default PortfolioSection;